// Recettes (lot L2-A) : catalogue des taxes et redevances (H4-03). Une rubrique porte sa nature,
// son fondement (« à confirmer » admis), sa périodicité de facturation et sa base de calcul ; ses
// tarifs sont datés, proposés par le financier puis approuvés par le maire ou président (quatre
// yeux). Un tarif approuvé n'est plus modifiable : un changement de tarif crée un nouveau tarif.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const code = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés');

export const NATURES_RUBRIQUE = { taxe: 'Taxe', redevance: 'Redevance', droit: 'Droit', frais: 'Frais' } as const;
export type NatureRubrique = keyof typeof NATURES_RUBRIQUE;

export const PERIODICITES_RUBRIQUE = {
  unique: 'Unique',
  journaliere: 'Journalière',
  mensuelle: 'Mensuelle',
  trimestrielle: 'Trimestrielle',
  annuelle: 'Annuelle',
} as const;
export type PeriodiciteRubrique = keyof typeof PERIODICITES_RUBRIQUE;

export const BASES_CALCUL = { forfait: 'Forfait', surface: 'Surface (m²)', unite: 'Unité', pourcentage: 'Pourcentage' } as const;
export type BaseCalcul = keyof typeof BASES_CALCUL;

export const CATEGORIES_RUBRIQUE = ['principale', 'exoneration', 'penalite'] as const;
export const STATUTS_TARIF = ['propose', 'approuve', 'suspendu'] as const;
export type StatutTarif = (typeof STATUTS_TARIF)[number];

/** Unité de temps d'un tarif : multiplicateur de la règle (« × 3 mois »), aucune pour un montant unique. */
export const UNITES_TEMPS_TARIF = ['aucune', 'jour', 'mois', 'trimestre', 'annee'] as const;
export type UniteTempsTarif = (typeof UNITES_TEMPS_TARIF)[number];

/** Mention portée par les tarifs de présentation dans le catalogue, les avis et les quittances (H4-03, HD-02). */
export const MENTION_TARIF_FICTIF_RECETTES = 'tarif fictif de présentation';
export const SUFFIXE_FICTIF = '(fictif)';

/** Montant en francs CFA, chiffres groupés par trois avec des espaces (« 27 000 F »). */
export function formaterFrancs(montant: number | bigint): string {
  const negatif = typeof montant === 'bigint' ? montant < 0n : montant < 0;
  const chiffres = (typeof montant === 'bigint' ? (negatif ? -montant : montant) : Math.abs(Math.trunc(montant))).toString();
  const groupes = chiffres.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${negatif ? '-' : ''}${groupes} F`;
}

export const schemaCreationRubrique = z
  .strictObject({
    code,
    libelle: z.string().trim().min(2).max(200),
    nature: z.enum(Object.keys(NATURES_RUBRIQUE) as [NatureRubrique, ...NatureRubrique[]]),
    fondement: z.string().trim().min(1).max(2000).default('à confirmer'),
    periodicite: z.enum(Object.keys(PERIODICITES_RUBRIQUE) as [PeriodiciteRubrique, ...PeriodiciteRubrique[]]),
    base_calcul: z.enum(Object.keys(BASES_CALCUL) as [BaseCalcul, ...BaseCalcul[]]),
    paiement_partiel_autorise: z.boolean().default(true),
    remboursable: z.boolean().default(false),
    categorie: z.enum(CATEGORIES_RUBRIQUE).default('principale'),
    rubrique_parent_id: uuid.optional(),
  })
  .refine((v) => (v.categorie === 'principale') === (v.rubrique_parent_id === undefined), {
    message: 'Une exonération ou une pénalité est liée à une rubrique principale',
    path: ['rubrique_parent_id'],
  });
export type CreationRubrique = z.infer<typeof schemaCreationRubrique>;

export const schemaModificationRubrique = z
  .strictObject({
    libelle: z.string().trim().min(2).max(200).optional(),
    fondement: z.string().trim().min(1).max(2000).optional(),
    paiement_partiel_autorise: z.boolean().optional(),
    remboursable: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Au moins un champ à modifier' });

export const schemaPropositionTarif = z
  .strictObject({
    montant: z.number().int().min(0).max(1_000_000_000_000).optional(),
    pourcentage: z.number().positive().max(100).multipleOf(0.001).optional(),
    unite: z.string().trim().min(1).max(100),
    unite_temps: z.enum(UNITES_TEMPS_TARIF).default('aucune'),
    debut: jour,
    fin: jour.optional(),
    acte_reference: z.string().trim().min(1).max(300).optional(),
    fictif: z.boolean().default(false),
  })
  .refine((v) => (v.montant === undefined) !== (v.pourcentage === undefined), { message: 'Indiquer un montant ou un pourcentage', path: ['montant'] })
  .refine((v) => !v.fin || v.fin >= v.debut, { message: 'La fin suit le début', path: ['fin'] });
export type PropositionTarif = z.infer<typeof schemaPropositionTarif>;

export const schemaApprobationTarif = z.strictObject({
  acte_reference: z.string().trim().min(1).max(300).optional(),
});

export const schemaSuspensionTarif = z.strictObject({ motif: z.string().trim().min(3).max(1000) });

export const schemaRubriqueReponse = z.object({
  id: z.string(),
  code: z.string(),
  libelle: z.string(),
  nature: z.string(),
  fondement: z.string(),
  periodicite: z.string(),
  base_calcul: z.string(),
  paiement_partiel_autorise: z.boolean(),
  remboursable: z.boolean(),
  categorie: z.string(),
  rubrique_parent_id: z.string().nullable(),
  active: z.boolean(),
  tarifs: z.array(
    z.object({
      id: z.string(),
      montant: z.number().nullable(),
      pourcentage: z.number().nullable(),
      unite: z.string(),
      unite_temps: z.string(),
      debut: z.string(),
      fin: z.string().nullable(),
      statut: z.string(),
      acte_reference: z.string().nullable(),
      fictif: z.boolean(),
      /** « tarif fictif de présentation » pour un tarif fictif, sinon nul. */
      mention: z.string().nullable(),
      propose_par: z.string().nullable(),
      approuve_par: z.string().nullable(),
      approuve_le: z.string().nullable(),
    }),
  ),
});
export type RubriqueReponse = z.infer<typeof schemaRubriqueReponse>;
